<template>
  <component :is="tag" class="landing-region-seo">
    <div class="section-inner">
      <template v-for="section in blok.body" :key="section._uid">
        <StoryblokComponent :blok="section" />
      </template>
      <div id="landing-region-seo-facilities">
        <FacilityCards
          :list="facilities"
          :include-result-number="true"
          :include-facility-type-group-key="type"
        />
      </div>
    </div>
  </component>
</template>

<script setup>
const props = defineProps({
  blok: {
    type: Object,
    required: true,
  },
  tag: {
    type: String,
    required: false,
    default: "div",
  },
  area: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  type: {
    type: Array,
    required: false,
    default: () => {
      return [];
    },
  },
});

const route = useRoute();
const config = useRuntimeConfig();
const countryPrefix = config.public["storyblokCountryPrefix"];

// Get Facilities by Region
const slug = `${countryPrefix}${route.path}`;

const currentPage = computed(() => parseInt(route.query.page) || 1);
const isAreaSelected = props.area.area_type && props.area.area_value;

const minNavHeight = computed(() => {
  return document.querySelector(".min-nav")?.offsetHeight;
});

const manageFocus = () => {
  const content = document.querySelector("#landing-region-seo-facilities");

  if (!content) return;

  const resetFocus = () => {
    content.setAttribute("tabindex", "0");
    content.focus();
    content.removeAttribute("tabindex");
  };

  resetFocus();
  window.scrollTo({
    top:
      content.getBoundingClientRect().top + window.scrollY - minNavHeight.value,
    behavior: "smooth",
  });
};

const serverSideFetchFacilities = async () => {
  return await useCustomAsyncStoryblok("", {
    version: config.public["storyblokVersion"],
    starts_with: isAreaSelected
      ? config.public["storyblokCountryPrefix"]
      : slug,
    filter_query: areaFilter(),
    per_page: 100,
  })
    .then((response) => {
      const facilities = response.value?.stories?.map((item) => ({
        address: item.content?.address,
        addressSupplement: null,
        city: item.content?.city,
        facilityId: item.content?.facilityId,
        facilityType: item.content?.centerType,
        facilityUniqueKey: item.content?.facilityUniqueKey,
        image:  item.content.body[0]?.images[0]?.asset?.filename || "",
        name: item.content?.commercialName,
        slug: item.full_slug,
        zipCode: item.content?.postalCode,
        facilityTypeGroupKey: item.content?.facilityTypeGroupKey,
      }));

      return {
        facilities,
      };
    })
    .catch((error) => {
      console.log(error);
    });
};

const areaFilter = () => {
  let filter;
  if(props.type?.length) {
    let types;
    props.type.forEach((item) => {
      types = types !== "" && types !== undefined ? `${types},${item}` : `${item}`;
    });
    filter = {
      facilityTypeGroupKey: {
        is: "not_empty_array",
        in: types,
      },
    };
  } else {
    filter = {
      facilityTypeGroupKey: {
        is: "not_empty_array",
      },
    };
  }

  switch (props.area?.area_type) {
    case "city":
      if(props.area?.area_value.includes("'")) {
        filter.address_search_slug_city = {
          in: useKebabCaseFilter(props.area?.area_value),
        };
      } else {
        filter.city = {
          in: props.area?.area_value,
        };
      }
      break;

    case "postalCode":
      filter.postalCode = {
        in: props.area?.area_value,
      };
      break;

    case "district":
    if(props.area?.area_value.includes("'")) {
        filter.address_search_slug_district = {
          in: useKebabCaseFilter(props.area?.area_value),
        };
      } else { 
        filter.district = {
          in: props.area?.area_value,
        };
      }
      break;

    case "departCode":
      filter.departCode = {
        in: props.area?.area_value,
      };
      break;

    case "region":
      filter.region = {
        in: props.area?.area_value,
      };
      break;
  }

  return filter;
};

const { facilities } = await serverSideFetchFacilities();

watch(currentPage, manageFocus);
</script>

<style lang="scss" scoped>
.landing-region-seo {
  .section-inner > *:not(:first-child) {
    padding-top: 4rem;
  }
  .results-number {
    margin-bottom: 0.75rem;
  }
}
#landing-region-seo-facilities {
  :deep(.results.section) {
    display: inherit;
    padding-top: 4rem;
  }
}
</style>
